// src/hooks/useMoodleQuiz.ts
import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query'; //-tech-debt-react-query;
import { moodleFetch, MoodleInstance } from './useMoodleHooks';
import { MoodleInstanceKey } from '../support/slices/moodleSlice';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';

// -------- Types --------

export type QuizSummary = {
  id: number;
  course: number;
  name: string;
  intro?: string;
  [key: string]: any;
};

export type QuizAccessInfo = {
  canattempt?: boolean;
  isfinished?: boolean;
  [key: string]: any;
};

export type QuizAttempt = {
  id: number;
  quiz: number;
  userid: number;
  state?: string;
  [key: string]: any;
};

export type AttemptPageQuestion = {
  slot: number;
  html: string;
  [key: string]: any;
};

export type AttemptData = {
  attempt?: QuizAttempt;
  questions?: AttemptPageQuestion[];
  nextpage?: number | null;
  previouspage?: number | null;
  [key: string]: any;
};

type PreflightItem = { name: string; value: string };
type PreflightInput = Record<string, string | number | boolean> | Array<PreflightItem>;
type NameValue = { name: string; value: string };

// -------- Helpers --------

function normalizePreflight(preflight?: PreflightInput | null): Array<PreflightItem> | undefined {
  if (!preflight) return undefined;
  if (Array.isArray(preflight)) {
    return preflight
      .filter((i) => i && typeof i.name === 'string' && typeof i.value !== 'undefined')
      .map((i) => ({ name: String(i.name), value: String(i.value) }));
  }
  return Object.entries(preflight).map(([name, value]) => ({ name, value: String(value) }));
}

function toNameValueList(payload?: Record<string, any> | Array<NameValue> | null): Array<NameValue> {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload
      .filter((i) => i && typeof (i as any).name === 'string' && typeof (i as any).value !== 'undefined')
      .map((i) => ({ name: String((i as any).name), value: String((i as any).value) }));
  }
  const list: Array<NameValue> = [];
  Object.entries(payload).forEach(([name, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => list.push({ name, value: String(v) }));
    } else {
      list.push({ name, value: String(value) });
    }
  });
  return list;
}

function splitProcessPayload(payload: Record<string, any> = {}) {
  const { finishattempt, timeup, preflightdata, page, ...rest } = payload || {};
  return {
    finishattempt: typeof finishattempt !== 'undefined' ? !!finishattempt : undefined,
    timeup: typeof timeup !== 'undefined' ? !!timeup : undefined,
    page: typeof page !== 'undefined' && !Number.isNaN(Number(page)) ? Number(page) : undefined,
    preflightdata: normalizePreflight(preflightdata),
    data: toNameValueList(rest),
  };
}

// -------- Quizzes by courses --------

export function useQuizzesByCourses(
  courseId?: number,
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any[], Error>({
    queryKey: ['MoodleQuizzesByCourses', courseId, instance],
    queryFn: async () => {
      if (!courseId) return [];
      const data = await moodleFetch<any>(
        'moodle/quizzes/by-courses/',
        {},
        {
          method: 'POST',
          body: JSON.stringify({ courseids: [courseId] }),
        },
        instance
      );

      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.quizzes)) return data.quizzes;
      return [];
    },
    ...{
        enabled: !!courseId,
        ...(options || {}),
      },
  });
}

// -------- Quiz access info --------

export function useQuizAccessInfo(
  quizId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any, Error>({
    queryKey: ['MoodleQuizAccessInfo', quizId, instance],
    queryFn: async () => {
      if (!quizId) throw new Error('No quizId');
      return moodleFetch<any>(
        `moodle/quiz/${quizId}/access-info/`,
        {},
        { method: 'POST' },
        instance
      );
    },
    ...{
        enabled: !!quizId,
        ...(options || {}),
      },
  });
}

// -------- User attempts --------

export function useQuizUserAttempts(
  quizId?: number,
  params?: { userid?: number; status?: string; includepreviews?: boolean },
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any, Error>({
    queryKey: ['MoodleQuizUserAttempts', quizId, params ?? {}, instance],
    queryFn: async () => {
      if (!quizId) throw new Error('No quizId');
      return moodleFetch<any>(
        `moodle/quiz/${quizId}/user-attempts/`,
        params || {},
        {},
        instance
      );
    },
    ...{
        enabled: !!quizId,
        ...(options || {}),
      },
  });
}

// -------- Start attempt --------

export function useQuizStartAttempt(
  options?: UseMutationOptions<any, Error, { quizId: number; forcenew?: boolean }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number; forcenew?: boolean }>({
    ...(options || {}),
    mutationFn: async ({ quizId, forcenew }) => {
      return moodleFetch<any>(
        `moodle/quiz/${quizId}/start-attempt/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({
            forcenew: !!forcenew,
          }),
        },
        instance
      );
    },
  });
}

// -------- Attempt data --------

export function useAttemptData(
  attemptId?: number,
  page?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any, Error>({
    queryKey: ['MoodleAttemptData', attemptId, page ?? 0, instance],
    queryFn: async () => {
      if (!attemptId) throw new Error('No attemptId');
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/data/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({ page: page ?? 0 }),
        },
        instance
      );
    },
    ...{
        enabled: !!attemptId,
        ...(options || {}),
      },
  });
}

// -------- Attempt summary --------

export function useAttemptSummary(
  attemptId?: number,
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<any, Error> {
  return useQuery<any, Error>({
    queryKey: ['moodleQuizAttemptSummary', attemptId, params, instance],
    queryFn: async () => {
      if (!attemptId) throw new Error('attemptId is required');
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/summary/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify(params || {}),
        },
        instance
      );
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!attemptId && (options?.enabled ?? true),
        ...(options || {}),
      },
  });
}


// -------- Attempt review --------

export function useAttemptReview(
  attemptId?: number,
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<any, Error> {
  return useQuery<any, Error>({
    queryKey: ['moodleQuizAttemptReview', attemptId, params, instance],
    queryFn: async () => {
      if (!attemptId) throw new Error('attemptId is required');
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/review/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify(params || {}),
        },
        instance
      );
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!attemptId && (options?.enabled ?? true),
        ...(options || {}),
      },
  });
}

// -------- Save / Process attempt --------

export function useAttemptSaveMutation(
  attemptId: number,
  options?: UseMutationOptions<any, Error, { fields: Record<string, any> }>,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();

  return useMutation<any, Error, { fields: Record<string, any> }>({
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['MoodleAttemptData', attemptId, instance] });
      if (options?.onSuccess) {
        options.onSuccess(...args);
      }
    },
    ...(options || {}),
    mutationFn: async ({ fields }) => {
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/save/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify(fields || {}),
        },
        instance
      );
    },
  });
}

export function useAttemptProcessMutation(
  attemptId: number,
  options?: UseMutationOptions<any, Error, Record<string, any>>,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();

  return useMutation<any, Error, Record<string, any>>({
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['MoodleAttemptData', attemptId, instance] });
      qc.invalidateQueries({ queryKey: ['MoodleAttemptSummary', attemptId, '{}', instance] });
      if (options?.onSuccess) {
        options.onSuccess(...args);
      }
    },
    ...(options || {}),
    mutationFn: async (payload) => {
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/process/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify(payload || {}),
        },
        instance
      );
    },
  });
}

// -------- View quiz / attempt "events" --------

export function useViewQuizMutation(
  _unused?: any,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number }>({
    mutationFn: async ({ quizId }) => {
      return moodleFetch<any>(`moodle/quiz/${quizId}/view/`, {}, { method: 'POST' }, instance);
    },
  });
}

export function useViewAttemptMutation(
  options?: UseMutationOptions<any, Error, { attemptId: number; page: number; preflightdata?: PreflightInput }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<
    any,
    Error,
    { attemptId: number; page: number; preflightdata?: PreflightInput }
  >({
    ...options,
    mutationFn: ({ attemptId, page, preflightdata }) => {
      const pf = normalizePreflight(preflightdata);
      const payload: any = { page: Number(page) };
      if (pf && pf.length) payload.preflightdata = pf;
      return moodleFetch<any>(
        `moodle/quiz/attempt/${Number(attemptId)}/view/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      );
    },
  });
}

export function useViewAttempt(
  attemptId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any, Error>({
    queryKey: ['MoodleViewAttempt', attemptId, instance],
    queryFn: async () => {
      if (!attemptId) throw new Error('No attemptId');
      return moodleFetch<any>(
        `moodle/quiz/attempt/${attemptId}/view/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({ page: 0 }),
        },
        instance
      );
    },
    ...{
        enabled: !!attemptId && (options?.enabled ?? true),
        ...(options || {}),
      },
  });
}

export function useViewAttemptReviewMutation(
  options?: UseMutationOptions<any, Error, { attemptId: number }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { attemptId: number }>({
    ...options,
    mutationFn: ({ attemptId }) =>
      moodleFetch<any>(
        `moodle/quiz/attempt/${Number(attemptId)}/review/view/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
  });
}

export function useViewAttemptSummaryMutation(
  options?: UseMutationOptions<any, Error, { attemptId: number; preflightdata?: PreflightInput }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { attemptId: number; preflightdata?: PreflightInput }>({
    ...options,
    mutationFn: ({ attemptId, preflightdata }) => {
      const pf = normalizePreflight(preflightdata);
      const payload: any = {};
      if (pf && pf.length) payload.preflightdata = pf;
      return moodleFetch<any>(
        `moodle/quiz/attempt/${Number(attemptId)}/summary/view/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      );
    },
  });
}

// -------- Other quiz APIs --------

export function useQuizCombinedReviewOptions(
  quizId?: number,
  params?: { userid?: number },
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const qid = Number(quizId);
  const body = useMemo(() => ({ userid: Number.isFinite(Number(params?.userid)) ? Number(params?.userid) : 0 }), [
    params?.userid,
  ]);

  return useQuery<any, Error>({
    queryKey: ['MoodleQuizCombinedReviewOptions', Number.isFinite(qid) ? qid : 'none', JSON.stringify(body), instance],
    queryFn: () =>
      moodleFetch<any>(
        `moodle/quiz/${qid}/combined-review-options/`,
        {},
        { method: 'POST', body: JSON.stringify(body) },
        instance
      ),
    ...{
        enabled: Number.isFinite(qid),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useQuizOverrides(
  quizId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const qid = Number(quizId);
  return useQuery<any, Error>({
    queryKey: ['MoodleQuizOverrides', Number.isFinite(qid) ? qid : 'none', instance],
    queryFn: () =>
      moodleFetch<any>(
        `moodle/quiz/${qid}/overrides/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
    ...{
        enabled: Number.isFinite(qid),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useQuizFeedbackForGrade(
  quizId?: number,
  grade?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const qid = Number(quizId);
  const body = useMemo(() => ({ grade: Number(grade) }), [grade]);

  return useQuery<any, Error>({
    queryKey: ['MoodleQuizFeedbackForGrade', Number.isFinite(qid) ? qid : 'none', JSON.stringify(body), instance],
    queryFn: () =>
      moodleFetch<any>(
        `moodle/quiz/${qid}/feedback-for-grade/`,
        {},
        { method: 'POST', body: JSON.stringify(body) },
        instance
      ),
    ...{
        enabled: Number.isFinite(qid) && Number.isFinite(Number(grade)),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useQuizRequiredQtypes(
  quizId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const qid = Number(quizId);
  return useQuery<any, Error>({
    queryKey: ['MoodleQuizRequiredQtypes', Number.isFinite(qid) ? qid : 'none', instance],
    queryFn: () =>
      moodleFetch<any>(
        `moodle/quiz/${qid}/required-qtypes/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
    ...{
        enabled: Number.isFinite(qid),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useQuizEditGradingPageData(
  quizId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const qid = Number(quizId);
  return useQuery<any, Error>({
    queryKey: ['MoodleQuizEditGradingPageData', Number.isFinite(qid) ? qid : 'none', instance],
    queryFn: () =>
      moodleFetch<any>(
        `moodle/quiz/${qid}/edit-grading-page-data/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
    ...{
        enabled: Number.isFinite(qid),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useQuizUserBestGrade(
  quizId?: number,
  userId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<any, Error> {
  return useQuery<any, Error>({
    queryKey: ['moodleQuizUserBestGrade', quizId, userId, instance],
    queryFn: async () => {
      if (!quizId) throw new Error('quizId is required');
      return moodleFetch<any>(
        `moodle/quiz/${quizId}/user-best-grade/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({ userid: userId ?? 0 }),
        },
        instance
      );
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!quizId && (options?.enabled ?? true),
        ...(options || {}),
      },
  });
}

// -------- Mutations: configure quiz --------

export function useQuizUpdateFilterConditionMutation(
  options?: UseMutationOptions<any, Error, { cmid: number; slotId: number; filtercondition: string }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { cmid: number; slotId: number; filtercondition: string }>(
    {
      ...options,
      mutationFn: ({ cmid, slotId, filtercondition }) =>
        moodleFetch<any>(
          `moodle/quiz/cm/${Number(cmid)}/slot/${Number(slotId)}/update-filter-condition/`,
          {},
          { method: 'POST', body: JSON.stringify({ filtercondition }) },
          instance
        ),
    }
  );
}

export function useQuizUpdateGradeItemsMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; quizgradeitems: Array<{ id: number; name?: string }> }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<
    any,
    Error,
    { quizId: number; quizgradeitems: Array<{ id: number; name?: string }> }
  >({
    ...options,
    mutationFn: ({ quizId, quizgradeitems }) =>
      moodleFetch<any>(
        `moodle/quiz/${Number(quizId)}/update-grade-items/`,
        {},
        { method: 'POST', body: JSON.stringify({ quizgradeitems }) },
        instance
      ),
  });
}

export function useQuizUpdateSlotsMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; slots: Array<{ id: number; displaynumber?: string; requireprevious?: number; maxmark?: number; quizgradeitemid?: number }> }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<
    any,
    Error,
    {
      quizId: number;
      slots: Array<{
        id: number;
        displaynumber?: string;
        requireprevious?: number;
        maxmark?: number;
        quizgradeitemid?: number;
      }>;
    }
  >({
    ...options,
    mutationFn: ({ quizId, slots }) =>
      moodleFetch<any>(
        `moodle/quiz/${Number(quizId)}/update-slots/`,
        {},
        { method: 'POST', body: JSON.stringify({ slots }) },
        instance
      ),
  });
}

export function useQuizSaveOverridesMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; overrides: Array<Record<string, any>> }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number; overrides: Array<Record<string, any>> }>(
    {
      ...options,
      mutationFn: ({ quizId, overrides }) =>
        moodleFetch<any>(
          `moodle/quiz/${Number(quizId)}/save-overrides/`,
          {},
          { method: 'POST', body: JSON.stringify({ overrides }) },
          instance
        ),
    }
  );
}

export function useQuizDeleteOverridesMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; overrideIds: number[] }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number; overrideIds: number[] }>({
    ...options,
    mutationFn: ({ quizId, overrideIds }) =>
      moodleFetch<any>(
        `moodle/quiz/delete-overrides/`,
        {},
        { method: 'POST', body: JSON.stringify({ quizid: Number(quizId), ids: overrideIds || [] }) },
        instance
      ),
  });
}

export function useQuizCreateGradeItemPerSectionMutation(
  options?: UseMutationOptions<any, Error, { quizId: number }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number }>({
    ...options,
    mutationFn: ({ quizId }) =>
      moodleFetch<any>(
        `moodle/quiz/${Number(quizId)}/create-grade-item-per-section/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
  });
}

export function useQuizCreateGradeItemsMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; quizgradeitems: Array<{ name: string }> }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<
    any,
    Error,
    { quizId: number; quizgradeitems: Array<{ name: string }> }
  >({
    ...options,
    mutationFn: ({ quizId, quizgradeitems }) =>
      moodleFetch<any>(
        `moodle/quiz/${Number(quizId)}/create-grade-items/`,
        {},
        { method: 'POST', body: JSON.stringify({ quizgradeitems }) },
        instance
      ),
  });
}

export function useQuizDeleteGradeItemsMutation(
  options?: UseMutationOptions<any, Error, { quizId: number; quizgradeitems: Array<{ id: number }> }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { quizId: number; quizgradeitems: Array<{ id: number }> }>(
    {
      ...options,
      mutationFn: ({ quizId, quizgradeitems }) =>
        moodleFetch<any>(
          `moodle/quiz/${Number(quizId)}/delete-grade-items/`,
          {},
          { method: 'POST', body: JSON.stringify({ quizgradeitems }) },
          instance
        ),
    }
  );
}

export function useQuizAddRandomQuestionsMutation(
  options?: UseMutationOptions<any, Error, { cmid: number; addonpage: number; randomcount: number; filtercondition?: string; newcategory?: string; parentcategory?: string }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<
    any,
    Error,
    {
      cmid: number;
      addonpage: number;
      randomcount: number;
      filtercondition?: string;
      newcategory?: string;
      parentcategory?: string;
    }
  >({
    ...options,
    mutationFn: ({
      cmid,
      addonpage,
      randomcount,
      filtercondition = '',
      newcategory = '',
      parentcategory = '0',
    }) =>
      moodleFetch<any>(
        `moodle/quiz/cm/${Number(cmid)}/add-random-questions/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({
            addonpage,
            randomcount,
            filtercondition,
            newcategory,
            parentcategory,
          }),
        },
        instance
      ),
  });
}

// -------- Reopen attempt --------

export function useReopenAttemptConfirmation(
  attemptId?: number,
  options?: Omit<UseQueryOptions<string, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  const aid = Number(attemptId);
  return useQuery<string, Error>({
    queryKey: ['MoodleReopenAttemptConfirmation', Number.isFinite(aid) ? aid : 'none', instance],
    queryFn: () =>
      moodleFetch<string>(
        `moodle/quiz/attempt/${aid}/reopen-confirmation/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
    ...{
        enabled: Number.isFinite(aid),
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useReopenAttemptMutation(
  options?: UseMutationOptions<any, Error, { attemptId: number }>,
  instance: MoodleInstance = 'external'
) {
  return useMutation<any, Error, { attemptId: number }>({
    ...options,
    mutationFn: ({ attemptId }) =>
      moodleFetch<any>(
        `moodle/quiz/attempt/${Number(attemptId)}/reopen/`,
        {},
        { method: 'POST', body: '{}' },
        instance
      ),
  });
}

